import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { AssistantForm } from "@/components/AssistantForm";
import { editAssistantSchema, EditAssistantFormValues } from "@/components/AssistantForm/schemas";
import { InternalGrid } from "@/styling/baseStyle";
import { useSocialCoachSession } from "@/utils";
import { AssistantCoach } from "@/services/bedrock/dtos/AssistantCoach";
import { AssistantCoachServices } from "@/services/bedrock/AssistantCoachServices";
import { useMemo } from "react";
import { toast } from "sonner";

interface OwnProps {
  assistant?: AssistantCoach;
  formSubmitRef?: any;
  withWritePermission?: boolean;

  onChangeHandler: () => void;
  successHandler: () => void;
  errorHandler: () => void;
}

type Props = OwnProps;

const AssistantEditFormFC: React.FC<Props> = ({
  assistant,
  formSubmitRef,
  onChangeHandler,
  successHandler,
  withWritePermission,
  errorHandler,
}) => {
  const { coachId } = useSocialCoachSession();

  const permissionsMap = useMemo(() => {
    if (!assistant?.componentPermissions) return {};
    return assistant.componentPermissions.reduce((map, p) => {
      if (p.componentId) {
        map[p.componentId.toString()] = p.permissionLevel;
      }
      return map;
    }, {} as Record<string, string>);
  }, [assistant?.componentPermissions]);

  const branchIds = useMemo(() => {
    return assistant?.branches?.map(d => d.id) || [];
  }, [assistant?.branches]);

  const regionIds = useMemo(() => {
    return assistant?.regions?.map(d => d.id) || [];
  }, [assistant?.regions]);

  const form = useForm<EditAssistantFormValues>({
    resolver: zodResolver(editAssistantSchema),
    defaultValues: {
      userId: assistant?.userId,
      firstName: assistant?.firstName || "",
      lastName: assistant?.lastName || "",
      email: assistant?.email || "",
      branches: branchIds,
      regions: regionIds,
      explicitPlayerIds: assistant?.explicitPlayerIds || [],
      isScoped: assistant?.isScoped || false,
      permissionsMap: permissionsMap,
    },
  });

  React.useEffect(() => {
    if (assistant) {
      form.reset({
        userId: assistant.userId,
        firstName: assistant.firstName || "",
        lastName: assistant.lastName || "",
        email: assistant.email || "",
        branches: branchIds,
        regions: regionIds,
        explicitPlayerIds: assistant.explicitPlayerIds || [],
        isScoped: assistant.isScoped || false,
        permissionsMap: permissionsMap,
      });
    }
  }, [assistant, permissionsMap, branchIds, regionIds, form]);

  React.useEffect(() => {
    const subscription = form.watch(() => {
      onChangeHandler();
    });
    return () => subscription.unsubscribe();
  }, [form, onChangeHandler]);

  const onSubmit = async (values: EditAssistantFormValues) => {
    const { firstName, lastName, email, regions, branches, explicitPlayerIds, isScoped } = values;

    try {
      const branchIds = (branches ?? []).map(branchId => Number(branchId));
      const regionIds = (regions ?? []).map(regionId => Number(regionId));

      await AssistantCoachServices.updateAssistantCoach(assistant!.userId, {
        firstName,
        lastName,
        email,
        branchIds,
        regionIds,
        explicitPlayerIds,
        isScoped,
        componentPermissions: values.permissionsMap,
      });
      toast.success("User updated successfully");
      successHandler();
    } catch (e: any) {
      console.error(e);
      form.setError("email", { message: e?.message || String(e) || "An error occurred" });
      errorHandler();
    }
  };

  React.useImperativeHandle(formSubmitRef, () => ({
    handleSubmit: () => form.handleSubmit(onSubmit)(),
  }));

  return (
    <InternalGrid>
      <AssistantForm form={form} coachId={coachId!} withWritePermission={withWritePermission} assistant={assistant} />
    </InternalGrid>
  );
};

export const AssistantEditForm = AssistantEditFormFC;
